import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";
import { useTranslation } from "react-i18next";
import { AuditRow, ShownReason } from "../../../models/AuditRow";
import { AUDIT_TOKENS } from "../../../theme/auditTokens";
import { rowReasons } from "../rowValues";
import { ReasonTaxClass } from "@/constants/ReasonTaxClasses";

const MAX_CHIPS = 3;

/** Dot in front of the reason: green when tax-favored, yellow when not, none when unassigned. */
const DOT_COLORS: Record<ReasonTaxClass, string | undefined> = {
  favored: AUDIT_TOKENS.dot.fresh,
  notFavored: AUDIT_TOKENS.dot.stale,
  none: undefined,
};

const chipSx = {
  height: 22,
  fontSize: 12,
  fontWeight: 600,
  bgcolor: AUDIT_TOKENS.none.bg,
  color: AUDIT_TOKENS.text,
} as const;

interface ChipProps {
  reason: ShownReason;
  title: string;
  onClick?: () => void;
  clickLabel?: string;
}

function ReasonChip({ reason, title, onClick, clickLabel }: ChipProps) {
  const color = DOT_COLORS[reason.taxClass];
  return (
    <Chip
      onClick={onClick}
      aria-label={clickLabel}
      label={
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
          {color && (
            <Box component="span" aria-label={title} sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
          )}
          {reason.label}
        </Box>
      }
      size="small"
      sx={{ ...chipSx, "&.Mui-focusVisible": { outline: AUDIT_TOKENS.focusRing, outlineOffset: 1 } }}
    />
  );
}

interface Props extends GridRenderCellParams<AuditRow> {
  /** Opens the map with the row's trips that carry this reason. */
  onShowReasonMap?: (row: AuditRow, reasonLabel: string) => void;
}

/**
 * Deduplicated reason chips, "+n" beyond three; trips without a reason leave the cell empty.
 * A click on a chip shows the trips with that reason on the map.
 */
export function ReasonChipsCell({ row, onShowReasonMap }: Props) {
  const { t } = useTranslation();
  const { reasons, missing } = rowReasons(row);
  const shown = reasons.slice(0, MAX_CHIPS);
  const overflow = reasons.length - shown.length;
  if (reasons.length === 0) return null;

  const tooltip = [
    ...reasons.map(reason => `${reason.label} (${t(`logbookAudit.taxClass.${reason.taxClass}`)})`),
    ...(missing ? [t("logbookAudit.grid.missingReason")] : []),
  ].join(", ");

  return (
    <Tooltip title={tooltip}>
      <Box sx={{ display: "flex", gap: "4px", alignItems: "center", overflow: "hidden" }}>
        {shown.map(reason => (
          <ReasonChip
            key={reason.label}
            reason={reason}
            title={t(`logbookAudit.taxClass.${reason.taxClass}`)}
            onClick={onShowReasonMap ? () => onShowReasonMap(row, reason.label) : undefined}
            clickLabel={onShowReasonMap ? t("logbookAudit.map.showReason", { reason: reason.label }) : undefined}
          />
        ))}
        {overflow > 0 && <Chip label={`+${overflow}`} size="small" sx={chipSx} />}
      </Box>
    </Tooltip>
  );
}
